<template>
	<div class="component-container component-notification-tray">
		<div v-click-outside="closeDropdown" class="notifications menu-item" @click="toggleActive">
			<i class="header-icon far fa-bell" />
			<p v-if="hasActiveNotifications" class="header-count" />
		</div>

		<div v-if="active" class="notifications-container">
			<div class="header">
				<p class="title">
					Notifications
				</p>
				<p class="icon" title="Clear All Notifications" @click.stop="clearAll">
					<i class="far fa-trash-can" />
				</p>
			</div>
			<div class="notifications-list type-active">
				<div
					v-for="(notification, index) in activeNotifications"
					:key="index"
					class="notification-item active"
					:class="[`bc-alert-${notification.type}`]"
					@click="goToContext(notification)"
				>
					<p class="title" v-text="notification.title" />
					<p class="message" v-text="notification.message" />

					<i
						v-if="notification.dismissible === false"
						class="pin-icon fas fa-thumbtack"
						title="This notification requires action and cannot be dismissed."
					/>
					<i
						v-else
						class="dismiss-icon fas fa-times"
						title="Dismiss notification"
						@click.stop="dismissOne(notification)"
					/>

					<div v-if="notification.progress !== undefined && notification.progress !== null" class="progress-bar-container">
						<div class="progress-bar">
							<div class="bar" :style="{ width: `${notification.progress}%` }">
								<!-- Empty -->
							</div>
							<div class="bar-pulse" :style="{ 'max-width': `${notification.progress}%` }">
								<!-- Empty -->
							</div>
						</div>
					</div>
				</div>

				<div
					v-for="(notification, index) in passiveNotifications"
					:key="index"
					class="notification-item passive"
					:class="[`bc-alert-${notification.type}`, { clickable: notification.url }]"
					@click="goToContext(notification)"
				>
					<p class="message" v-text="notification.message" />
					<p v-if="notification.repeated && notification.repeated > 0" class="repeated" v-text="`x${notification.repeated}`" />
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, inject, ref } from 'vue';
	import { useStore } from 'vuex';
	import { useRouter } from 'vue-router';
	import type { HttpHelper } from '../helper/http';
	import type { INotification } from '../store/modules/notification';

	const active = ref(false);
	const store = useStore();
	const router = useRouter();
	const http = inject<HttpHelper>('http');

	if (!http) throw new Error('HttpHelper (inject \'http\') is not provided');

	const hasActiveNotifications = computed(() => {
		return store.state.notification.activeNotifications.length > 0;
	});

	const activeNotifications = computed(() => {
		return store.state.notification.activeNotifications;
	});

	const passiveNotifications = computed(() => {
		return store.state.notification.pastNotifications;
	});

	const goToContext = (notification: INotification) => {
		if (!notification?.url) return;

		router.push(notification.url);
		active.value = false;
	};

	const dismissOne = (notification: INotification) => {
		if (!notification.dismissKey) return;

		store.dispatch('notification/dismissOne', { dismissKey: notification.dismissKey, http });
	};

	const clearAll = () => {
		store.dispatch('notification/dismissAll', { http });
	};

	const toggleActive = () => {
		active.value = !active.value;
	};

	const closeDropdown = () => {
		active.value = false;
	};
</script>

<style lang="scss" scoped>
	@use 'sass:color';
	@use '../assets/styles/base' as base;

	.component-container {
		text-align: center;
	}

	.menu-item {
		text-align: center;
		height: 32px;
		width: 32px;
		display: inline-block;
		border-radius: 50%;
		padding-top: 9px;
		margin-top: 2px;
		position: relative;
		cursor: pointer;
		color: white;
		background-color: base.$sidebar-create-color;
		@include base.prefix(transition, background-color 0.5s ease-in-out);
		&:hover {
			background-color: color.adjust(base.$sidebar-create-color, $lightness: -10%);
			@include base.prefix(transition, background-color 0.3s ease-in-out);
		}

		.header-icon {
			font-size: 16px;
			position: relative;
			top: -1px;
		}

		.header-count {
			font-size: 12px;
			position: absolute;
			height: 10px;
			width: 10px;
			top: 4px;
			right: -2px;
			background-color: #25c281;
			border-radius: 50%;
		}
	}

	.notifications-container {
		position: absolute;
		top: calc(#{base.$header-height} + 10px);
		width: 300px;
		right: 20px;
		background-color: white;
		border-radius: 10px 6px 10px 10px;
		@include base.clearfix();
		@include base.prefix(box-shadow, 0 3px 5px 0 rgba(base.$sidebar-create-color, 0.3));

		.header {
			padding: 10px;
			@include base.clearfix();

			.title {
				float: left;
				width: auto;
				color: base.$sidebar-controller-color;
				font-weight: bold;
			}

			.icon {
				float: right;
				width: auto;
				cursor: pointer;
				color: base.$sidebar-create-color;
				&:hover {
					color: color.adjust(base.$sidebar-create-color, $lightness: -10%);
				}
			}
		}

		.notifications-list {
			max-height: 450px;
			overflow-y: auto;
		}

		&:after {
			content: '';
			width: 0;
			height: 0;
			border-left: 8px solid transparent;
			border-right: 8px solid transparent;
			border-bottom: 8px solid white;
			position: absolute;
			top: -8px;
			right: 63px;
		}

		.notification-item {
			float: left;
			text-align: left;
			margin: 0.5rem 0.5rem 0px 0.5rem;
			padding: 10px;
			background-color: rgba(base.$sidebar-create-color, 0.05);
			width: calc(100% - 1rem);
			border-radius: 8px;
			border-left: 8px solid #e3e3e3;
			@include base.prefix(transition, all 0.2s ease-in-out);
			&:last-of-type {
				margin-bottom: 0.5rem !important;
			}
			&:hover {
				background-color: rgba(base.$sidebar-create-color, 0.1);
				.progress-bar {
					background-color: color.adjust(#e3e3e3, $lightness: -2%) !important;
				}
			}

			&:hover.type-active {
				background-color: #e3e3e3;
			}

			&.passive {
				padding-right: 30px;
				position: relative;

				.message {
					font-size: 12px;
					font-weight: 400;
					color: base.$sidebar-controller-color;
				}

				.repeated {
					position: absolute;
					top: 10px;
					right: 10px;
					color: rgba(base.$sidebar-create-color, 0.6);
					font-size: 12px;
				}
			}

			&.passive.clickable {
				cursor: pointer;
			}

			&.active {
				border: none;
				position: relative;

				.title {
					float: left;
					width: 80%;
					text-align: left;
					font-weight: 600;
					font-size: 12px;
					padding-bottom: 5px;
					padding-left: 10px;
					color: base.$sidebar-controller-color;
				}

				.message {
					float: left;
					width: 100%;
					font-weight: normal;
					font-size: 12px;
					padding-left: 10px;
					color: rgba(base.$sidebar-controller-color, 0.7);
				}

				.pin-icon {
					position: absolute;
					right: 10px;
					top: 10px;
					color: rgba(base.$sidebar-create-color, 0.7);
					font-size: 12px;
				}

				.dismiss-icon {
					position: absolute;
					right: 10px;
					top: 10px;
					color: rgba(base.$sidebar-create-color, 0.5);
					font-size: 12px;
					cursor: pointer;
					&:hover {
						color: base.$sidebar-create-color;
					}
				}

				.progress-bar-container {
					float: left;
					width: 100%;
					padding: 10px 10px 0px 10px;

					.progress-bar {
						position: relative;
						height: 8px;
						border-radius: 4px;
						background-color: #e3e3e3;

						.bar {
							height: 8px;
							border-radius: 4px;
							background-color: rgba(base.$alert-success-colour, 0.7);
							width: 0%;
							@include base.prefix(transition, all 0.2s ease-in-out);
						}

						.bar-pulse {
							position: absolute;
							top: 0;
							left: 0;
							@include base.prefix(animation-name, progress-bar-pulse);
							@include base.prefix(animation-duration, 2s);
							@include base.prefix(animation-iteration-count, infinite);
							height: 8px;
							border-radius: 4px;
							background-color: base.$alert-success-colour;
							@include base.prefix(transition, all 0.2s ease-in-out);
						}
					}
				}
			}
		}
	}
</style>
